import { faFilter } from '@fortawesome/pro-light-svg-icons/faFilter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  activeFilterCount,
  AgentStatusBucket,
  CHANNEL_FILTER_ORDER,
  EMPTY_FILTERS,
  STATUS_BUCKET_ORDER,
} from './agents_liveboard_filtering';
import {
  ClearButton,
  ControlContainer,
  Panel,
  PanelFooter,
  PanelRow,
  PanelSection,
  PanelSectionTitle,
  PillButton,
} from './liveboard_control_styles';
import Checkbox from 'components/common/checkbox';
import useCloseOnOutsideClick from './use_close_on_outside_click';
import { RoutingChannel } from 'models/agent_routing_preferences';

function channelLabel(t, channel) {
  switch (channel) {
    case RoutingChannel.MESSAGING:
      return t('Messaging');
    case RoutingChannel.MAIL:
      return t('Email');
    case RoutingChannel.VOICE:
      return t('Voice');
    case RoutingChannel.TASK:
      return t('Task');
    default:
      return channel;
  }
}

function statusLabel(t, bucket) {
  switch (bucket) {
    case AgentStatusBucket.AVAILABLE:
      return t('Available');
    case AgentStatusBucket.IN_CONVERSATION:
      return t('In conversation');
    case AgentStatusBucket.ON_CALL:
      return t('On a call');
    case AgentStatusBucket.FOCUS:
      return t('Focus');
    case AgentStatusBucket.IDLE:
      return t('Idle');
    case AgentStatusBucket.AWAY:
      return t('Away');
    default:
      return bucket;
  }
}

function toggleValue(list, value) {
  return list.indexOf(value) === -1 ? [...list, value] : list.filter(item => item !== value);
}

export default function AgentsLiveboardFilterBar({ filters, onChange }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  useCloseOnOutsideClick(
    containerRef,
    useCallback(() => setIsOpen(false), []),
    isOpen
  );

  const count = activeFilterCount(filters);

  const setDimension = (key, value) => onChange({ ...filters, [key]: toggleValue(filters[key], value) });
  const clear = () => onChange({ ...EMPTY_FILTERS });

  return (
    <ControlContainer ref={containerRef}>
      <PillButton active={count > 0} data-aid="agentsLiveboardFilter-toggle" onClick={() => setIsOpen(o => !o)}>
        <FontAwesomeIcon icon={faFilter} />
        {t('Filter')}
        {count > 0 ? ` (${count})` : ''}
      </PillButton>
      {isOpen && (
        <Panel data-aid="agentsLiveboardFilter-panel">
          <PanelSection>
            <PanelSectionTitle>{t('Channel')}</PanelSectionTitle>
            {CHANNEL_FILTER_ORDER.map(channel => (
              <PanelRow key={channel}>
                <Checkbox
                  checked={filters.channels.indexOf(channel) !== -1}
                  data-aid={`agentsLiveboardFilter-channel-${channel}`}
                  label={channelLabel(t, channel)}
                  onChange={() => setDimension('channels', channel)}
                />
              </PanelRow>
            ))}
          </PanelSection>
          <PanelSection>
            <PanelSectionTitle>{t('Status')}</PanelSectionTitle>
            {STATUS_BUCKET_ORDER.map(bucket => (
              <PanelRow key={bucket}>
                <Checkbox
                  checked={filters.statuses.indexOf(bucket) !== -1}
                  data-aid={`agentsLiveboardFilter-status-${bucket}`}
                  label={statusLabel(t, bucket)}
                  onChange={() => setDimension('statuses', bucket)}
                />
              </PanelRow>
            ))}
          </PanelSection>
          {count > 0 && (
            <PanelFooter>
              <ClearButton data-aid="agentsLiveboardFilter-reset" onClick={clear}>
                {t('Reset')}
              </ClearButton>
            </PanelFooter>
          )}
        </Panel>
      )}
    </ControlContainer>
  );
}

AgentsLiveboardFilterBar.propTypes = {
  filters: PropTypes.shape({
    channels: PropTypes.arrayOf(PropTypes.string).isRequired,
    statuses: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};
